import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./ProductDetails.css";
import Navbar from "../../Components/Navbar/Navbar";
import Footer from "../../Components/Footer/Footer";
import { useStateValue } from "../../StateProvider";
import {
  Share2,
  X,
  Phone,
  PhoneOff,
  Copy,
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  ShoppingCart,
  ArrowLeft,
  Twitter,
  Linkedin,
} from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTitle } from "../../hooks/useTitle";
import SEO from "../../Components/SEO/SEO";
import Recommended from "./Recommended";
import LoadingScreen from "../../LoadingScreen";
import { account, tablesDB, DB_ID, PRODUCTS_TABLE } from "../../appwrite";
import { Query } from "appwrite";

const MAX_QTY = 3;

const fmtPrice = (p) =>
  parseFloat(p || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtCat = (slug) =>
  (slug || "")
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

function deslugify(slug) {
  if (!slug) return "";
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export default function ProductDetails() {
  const { productName } = useParams();
  const navigate = useNavigate();
  const [{ cart }, dispatch] = useStateValue();

  const [product, setProduct] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [imgIndex, setImgIndex] = useState(0);
  const [recommended, setRecommended] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [shareOpen, setShareOpen] = useState(false);
  const [showNumber, setShowNumber] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useTitle(`${deslugify(productName)} - TrendBay`);

  useEffect(() => {
    account
      .get()
      .then(() => setIsLoggedIn(true))
      .catch(() => setIsLoggedIn(false));
  }, []);

  useEffect(() => {
    setShowNumber(false);
  }, [productName]);

  useEffect(() => {
    (async () => {
      try {
        const res = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: PRODUCTS_TABLE,
          queries: [Query.equal("slug", productName)],
        });
        if (!res.rows || res.rows.length === 0) {
          navigate("/shop/all");
          return;
        }
        const p = res.rows[0];
        setProduct(p);

        const imgs = [p.productImage, ...(p.moreImages || [])].filter(Boolean);
        setGallery(imgs);
        setImgIndex(0);

        const recRes = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: PRODUCTS_TABLE,
          queries: [
            Query.equal("category", p.category),
            Query.notEqual("$id", p.$id),
          ],
        });
        setRecommended(
          (recRes.rows || []).sort(() => 0.5 - Math.random()).slice(0, 4),
        );

        const inCart = cart.find((i) => i.id === p.$id);
        setAddedToCart(!!inCart);
        setQuantity(inCart ? inCart.quantity : 1);
      } catch {
        navigate("/shop/all");
      }
    })();
  }, [productName]);

  useEffect(() => {
    const inCart = cart.find((i) => i.id === product?.$id);
    setAddedToCart(!!inCart);
    if (inCart) setQuantity(inCart.quantity);
  }, [cart, product]);

  const addToCart = () => {
    dispatch({
      type: "ADD_TO_CART",
      item: {
        id: product.$id,
        title: product.productName,
        image: product.productImage,
        price: parseFloat(product.price),
        category: product.category,
        available: product.available,
        quantity: 1,
      },
    });
    setAddedToCart(true);
    setQuantity(1);
    toast.success(`${product.productName} added to cart!`, { autoClose: 1800 });
  };

  const maxQty = product ? (product.quantity > 0 ? product.quantity : MAX_QTY) : MAX_QTY;

  const updateQty = (n) => {
    if (n < 1) {
      dispatch({ type: "REMOVE_FROM_CART", id: product.$id });
      setAddedToCart(false);
    } else if (n <= maxQty) {
      dispatch({ type: "UPDATE_QUANTITY", id: product.$id, quantity: n });
      setQuantity(n);
    } else {
      toast.warn(`Max ${maxQty} available`, { autoClose: 1500 });
    }
  };

  const handleShare = (url) => window.open(url, "_blank");

  const copyLink = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(window.location.href)
        .then(() => toast.success("Link copied!"))
        .catch(() => toast.error("Failed to copy"));
    } else {
      const el = document.createElement("input");
      el.value = window.location.href;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      toast.success("Link copied!");
    }
  };

  const prevImg = () =>
    setImgIndex((i) => (i === 0 ? gallery.length - 1 : i - 1));
  const nextImg = () =>
    setImgIndex((i) => (i === gallery.length - 1 ? 0 : i + 1));

  if (!product) return <LoadingScreen />;

  const isReup = product.category === "reup-vault";
  const isSoldOut = product.sold || !product.available;
  const catLink = `/shop/${product.category}`;

  /* ── SEO data ────────────────────────────────────────── */
  const SITE_URL = "https://mart.trendbayds.com";
  const productUrl = `/product/${productName}`;
  const productImages = [product.productImage, ...(product.moreImages || [])].filter(Boolean);
  const conditionMap = {
    "New": "https://schema.org/NewCondition",
    "Used": "https://schema.org/UsedCondition",
    "Refurbished": "https://schema.org/RefurbishedCondition",
  };
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.productName,
    "description": product.productDescription || product.productName,
    "image": productImages,
    "sku": product.sku || product.$id,
    "brand": { "@type": "Brand", "name": "TrendBay DS" },
    "offers": {
      "@type": "Offer",
      "url": `${SITE_URL}${productUrl}`,
      "priceCurrency": "NGN",
      "price": String(parseFloat(product.price || 0)),
      "availability": isSoldOut
        ? "https://schema.org/OutOfStock"
        : "https://schema.org/InStock",
      "itemCondition": conditionMap[product.productCondition] || "https://schema.org/NewCondition",
      "seller": { "@type": "Organization", "name": "TrendBay DS" },
      "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
    },
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "Shop", "item": `${SITE_URL}/shop` },
      { "@type": "ListItem", "position": 3, "name": fmtCat(product.category), "item": `${SITE_URL}${catLink}` },
      { "@type": "ListItem", "position": 4, "name": product.productName, "item": `${SITE_URL}${productUrl}` },
    ],
  };
  const seoDesc = product.productDescription
    ? product.productDescription.slice(0, 155).replace(/\n/g, " ")
    : `Buy ${product.productName} on TrendBay Mart. Fast delivery across Nigeria. ${isSoldOut ? "Currently out of stock." : `Only ₦${fmtPrice(product.price)}.`}`;

  const renderDescription = () => {
    const text = product.productDescription || "";
    const MAX = 280;
    if (text.length <= MAX || expanded) {
      return (
        <p className="pd-desc-text" style={{ whiteSpace: "pre-line" }}>
          {text}
        </p>
      );
    }
    return (
      <p className="pd-desc-text" style={{ whiteSpace: "pre-line" }}>
        {text.slice(0, MAX)}
        <span className="pd-read-more" onClick={() => setExpanded(true)}>
          … Read more
        </span>
      </p>
    );
  };

  return (
    <div className="pd-page">
      <SEO
        title={product.productName}
        description={seoDesc}
        image={product.productImage}
        path={productUrl}
        type="product"
        product={{
          price: parseFloat(product.price || 0),
          currency: "NGN",
          availability: isSoldOut ? "out of stock" : "in stock",
          condition: (product.productCondition || "new").toLowerCase(),
        }}
        jsonLd={[productSchema, breadcrumbSchema]}
      />
      <Navbar />

      <div className="pd-wrap">
        {/* Breadcrumb */}
        <nav className="pd-crumb">
          <a href="/">Home</a>
          <span>/</span>
          <a href="/shop">Shop</a>
          <span>/</span>
          <a href={catLink}>{fmtCat(product.category)}</a>
          <span>/</span>
          <span className="pd-crumb-current">{product.productName}</span>
        </nav>

        {/* Back */}
        <button className="pd-back" onClick={() => navigate(-1)}>
          <ArrowLeft size={15} /> Back
        </button>

        {/* Main layout */}
        <div className="pd-layout">
          {/* Gallery */}
          <div className="pd-gallery">
            <div className="pd-gallery__main">
              <img
                src={gallery[imgIndex] || product.productImage}
                alt={product.productName}
                className="pd-gallery__img"
              />
              {gallery.length > 1 && (
                <>
                  <button
                    className="pd-gallery__arrow pd-gallery__arrow--l"
                    onClick={prevImg}
                  >
                    <ChevronLeft size={22} />
                  </button>
                  <button
                    className="pd-gallery__arrow pd-gallery__arrow--r"
                    onClick={nextImg}
                  >
                    <ChevronRight size={22} />
                  </button>
                  <span className="pd-gallery__counter">
                    {imgIndex + 1} / {gallery.length}
                  </span>
                </>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="pd-gallery__thumbs">
                {gallery.map((src, i) => (
                  <div
                    key={i}
                    className={`pd-thumb ${i === imgIndex ? "pd-thumb--active" : ""}`}
                    onClick={() => setImgIndex(i)}
                  >
                    <img src={src} alt="" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="pd-info">
            {/* Meta */}
            <div className="pd-info__meta">
              <a href={catLink} className="pd-cat">
                {fmtCat(product.category)}
              </a>
              {product.productCondition &&
                product.productCondition !== "New" && (
                  <span className="pd-condition">
                    {product.productCondition}
                  </span>
                )}
              {product.sku && (
                <span className="pd-sku">SKU: {product.sku}</span>
              )}
            </div>

            <h1 className="pd-title">{product.productName}</h1>

            {isReup && product.sellerName && (
              <p className="pd-seller">
                Sold by{" "}
                <a href={`/seller/${product.sellerId}`} className="pd-seller-link">
                  {product.sellerName}
                </a>
              </p>
            )}

            {isReup && product.trustTags && product.trustTags.length > 0 && (
              <div className="pd-trust-tags">
                {product.trustTags.map((tag) => {
                  const labels = { authentic: "Authentic", withBox: "With Box", withReceipt: "With Receipt", sealed: "Sealed" };
                  return (
                    <span key={tag} className={`pd-trust-tag pd-trust-tag--${tag}`}>
                      ✓ {labels[tag] || tag}
                    </span>
                  );
                })}
              </div>
            )}

            <div className="pd-price">₦{fmtPrice(product.price)}</div>

            {!isReup && (
              <p className="pd-trust-note">
                We confirm delivery after assigning a rider - you always pay the
                fairest price.
              </p>
            )}

            {/* Qty stepper when in cart */}
            {addedToCart && (
              <div className="pd-stepper">
                <button onClick={() => updateQty(quantity - 1)}>
                  <Minus size={14} />
                </button>
                <span>{quantity}</span>
                <button
                  onClick={() => updateQty(quantity + 1)}
                  disabled={quantity >= maxQty}
                >
                  <Plus size={14} />
                </button>
                {quantity >= maxQty && (
                  <small className="pd-max">Max {maxQty}</small>
                )}
              </div>
            )}

            {/* CTA */}
            <button
              className={`pd-cta-btn ${addedToCart ? "pd-cta-btn--incart" : "pd-cta-btn--add"}`}
              onClick={addedToCart ? () => navigate("/cart") : addToCart}
              disabled={isSoldOut}
            >
              <ShoppingCart size={16} />
              {isSoldOut
                ? product.sold
                  ? "SOLD"
                  : "Out of Stock"
                : addedToCart
                  ? "View Cart"
                  : "Add to Cart"}
            </button>

            {/* Share */}
            <button className="pd-share-btn" onClick={() => setShareOpen(true)}>
              <Share2 size={14} /> Share product
            </button>

            {/* Description */}
            <div className="pd-description">
              <h3 className="pd-desc-title">Description</h3>
              {renderDescription()}
              {expanded && (
                <span
                  className="pd-read-more"
                  onClick={() => setExpanded(false)}
                >
                  Show less
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Recommended */}
        <Recommended recommendedProducts={recommended} />
      </div>

      {/* Mobile sticky CTA bar - fixed above bottom nav */}
      <div className="pd__mobile-cta">
        {addedToCart && (
          <div className="pd__mobile-stepper">
            <button onClick={() => updateQty(quantity - 1)}><Minus size={16} /></button>
            <span>{quantity}</span>
            <button onClick={() => updateQty(quantity + 1)} disabled={quantity >= maxQty}><Plus size={16} /></button>
          </div>
        )}
        <button
          className={`pd__mobile-cta-btn ${addedToCart ? "pd-cta-btn--incart" : "pd-cta-btn--add"}`}
          onClick={addedToCart ? () => navigate("/cart") : addToCart}
          disabled={isSoldOut}
        >
          <ShoppingCart size={16} />
          {isSoldOut
            ? product.sold ? "SOLD" : "Out of Stock"
            : addedToCart ? "View Cart" : "Add to Cart"}
        </button>
      </div>

      {/* Share modal */}
      {shareOpen && (
        <div className="pd-share-overlay" onClick={() => setShareOpen(false)}>
          <div className="pd-share-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="pd-share-header">
              <span>Share this product</span>
              <button onClick={() => setShareOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <p className="pd-share-name">{product.productName}</p>
            <div className="pd-share-btns">
              <button
                className="pd-share-icon pd-share-icon--wa"
                onClick={() =>
                  handleShare(
                    `whatsapp://send?text=${encodeURIComponent(product.productName + " – " + window.location.href)}`,
                  )
                }
              >
                WhatsApp
              </button>
              <button
                className="pd-share-icon pd-share-icon--tw"
                onClick={() =>
                  handleShare(
                    `https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent("Check out " + product.productName + " on TrendBay!")}`,
                  )
                }
              >
                <Twitter size={14} /> Twitter
              </button>
              <button
                className="pd-share-icon pd-share-icon--li"
                onClick={() =>
                  handleShare(
                    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`,
                  )
                }
              >
                <Linkedin size={14} /> LinkedIn
              </button>
              <button
                className="pd-share-icon pd-share-icon--copy"
                onClick={copyLink}
              >
                <Copy size={14} /> Copy Link
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
